// pages/UserAdmin/crm/OrigemContato.js — a seção "Origem" da janela do
// contato (out/26, rastreamento de leads PR 1).
//
// Mostra de qual anúncio "Clique para o WhatsApp" o contato chegou: o que o
// webhook gravou do `referral` da primeira mensagem depois do clique
// (services/origem_lead.py). A lista do CRM já traz a chegada mais recente
// (`origem_anuncio`, com `total`); as anteriores só são pedidas quando a
// pessoa abre "Ver todas", pela rota do histórico.
//
// O link abre em outra aba com `noopener noreferrer`: o servidor só guarda
// http(s), mas a página do anúncio é da Meta, não da Cetto.

import { useState } from 'react';
import { Box } from '@mui/material';
import { FiExternalLink, FiTarget } from 'react-icons/fi';
import api from '../../../services/Api';
import { OUTFIT, rotulo } from './theme';

const foco = { '&:focus-visible': { outline: '2px solid var(--ch-green)', outlineOffset: 2 } };

// "Anúncio no Instagram", "Publicação no Facebook"… A Meta não diz a rede;
// o servidor tira do link, e sem link fica só "Anúncio".
function rede(o) {
  return o.rede === 'instagram' ? ' no Instagram' : o.rede === 'facebook' ? ' no Facebook' : '';
}
const ondeVeio = (o) => `${o.tipo === 'post' ? 'Publicação' : 'Anúncio'}${rede(o)}`;
const veioDe = (o) => `${o.tipo === 'post' ? 'Veio da publicação' : 'Veio do anúncio'}${rede(o)}`;

function VerAnuncio({ link }) {
  if (!link) return null;
  return (
    <Box component="a" href={link} target="_blank" rel="noopener noreferrer"
      sx={{
        ...foco, display: 'inline-flex', alignItems: 'center', gap: '6px', borderRadius: '6px',
        fontSize: 13.5, fontWeight: 600, color: 'var(--ch-purple-d)', textDecoration: 'none',
        '&:hover': { textDecoration: 'underline' },
      }}>
      Ver anúncio <FiExternalLink size={13} aria-hidden />
    </Box>
  );
}

const linhasMax = (n) => ({
  display: '-webkit-box', WebkitLineClamp: n, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere',
});

export default function OrigemContato({ contato, cartao, fmtDate }) {
  const origem = contato?.origem_anuncio;
  // null = ainda não pediu; [] em diante = o histórico inteiro.
  const [todas, setTodas] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(false);

  if (!origem) return null;

  const verTodas = async () => {
    if (todas) { setTodas(null); return; }
    setCarregando(true);
    setErro(false);
    try {
      const r = await api.get(`/contacts/${contato.id}/origens`);
      setTodas(r.data?.items || []);
    } catch {
      setErro(true);
    } finally {
      setCarregando(false);
    }
  };

  // A primeira do histórico é a que o cartão já mostra.
  const anteriores = (todas || []).filter((o) => o.id !== origem.id);

  return (
    <Box>
      <Box component="span" sx={{ ...rotulo, display: 'block', mb: '12px' }}>Origem</Box>
      <Box sx={{ ...cartao, display: 'flex', gap: '12px' }}>
        <Box component="span" aria-hidden sx={{
          width: 36, height: 36, borderRadius: '10px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: 'var(--ch-purple-bg)', color: 'var(--ch-purple-d)', border: '1px solid var(--ch-purple-b)',
        }}>
          <FiTarget size={16} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Box component="span" sx={{ fontSize: 13, fontWeight: 600, color: 'var(--ch-purple-d)' }}>
            {veioDe(origem)}
          </Box>
          {origem.titulo && (
            <Box component="span" sx={{ fontSize: 14.5, fontWeight: 600, ...linhasMax(2) }}>
              {origem.titulo}
            </Box>
          )}
          {origem.texto && (
            <Box component="span" sx={{ fontSize: 13.5, color: 'var(--ch-text2)', lineHeight: 1.5, ...linhasMax(3) }}>
              {origem.texto}
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 14px', mt: '4px' }}>
            <Box component="span" sx={{ fontSize: 13, color: 'var(--cx-cap)', fontVariantNumeric: 'tabular-nums' }}>
              Chegou em {fmtDate(origem.chegou_em)}
            </Box>
            <VerAnuncio link={origem.link} />
          </Box>

          {origem.total > 1 && (
            <Box component="button" type="button" onClick={verTodas} aria-expanded={Boolean(todas)} disabled={carregando}
              sx={{
                ...foco, alignSelf: 'flex-start', mt: '6px', p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer',
                fontFamily: OUTFIT, fontSize: 13.5, fontWeight: 600, color: 'var(--ch-text2)', borderRadius: '6px',
                '&:hover': { color: 'var(--ch-text)' }, '&:disabled': { cursor: 'default', opacity: 0.6 },
              }}>
              {carregando ? 'Carregando…' : todas ? 'Ocultar as anteriores' : `Ver todas (${origem.total})`}
            </Box>
          )}
          {erro && (
            <Box component="span" role="alert" sx={{ fontSize: 13, color: 'var(--ch-red-d)' }}>
              Não foi possível carregar as chegadas anteriores. Tente de novo.
            </Box>
          )}
          {todas && anteriores.length > 0 && (
            <Box component="ul" sx={{ listStyle: 'none', m: '8px 0 0', p: 0, display: 'flex', flexDirection: 'column' }}>
              {anteriores.map((o) => (
                <Box component="li" key={o.id} sx={{ py: '10px', borderTop: '1px solid var(--ch-bdr)', display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                  <Box component="span" sx={{ fontSize: 14, fontWeight: 600, ...linhasMax(1) }}>
                    {o.titulo || ondeVeio(o)}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 14px' }}>
                    <Box component="span" sx={{ fontSize: 13, color: 'var(--cx-cap)', fontVariantNumeric: 'tabular-nums' }}>
                      {o.titulo ? `${ondeVeio(o)} · ` : ''}{fmtDate(o.chegou_em)}
                    </Box>
                    <VerAnuncio link={o.link} />
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
